(async () => {
  const root = document.querySelector('[data-fp-discovery][data-section-id]');
  if (!root || root.dataset.refreshReady === 'true') return;
  root.dataset.refreshReady = 'true';
  if (window.Shopify?.designMode) return;

  if (window.FiivePersonalization?.ready) await Promise.race([window.FiivePersonalization.ready, new Promise(resolve => setTimeout(resolve, 400))]);
  const rankStarted = performance.now();
  root.dataset.discoveryVersion = "3.5.2";
  const panels = [...root.querySelectorAll('[data-discovery-panel]')];
  const allCards = panel => [...panel.querySelectorAll('.fp-discovery__grid [data-discovery-card]')];
  const personalization = window.FiivePersonalization;
  const categories = new Map();
  panels.forEach(panel => allCards(panel).forEach(card => {
    const handle = card.dataset.productHandle;
    if (!categories.has(handle)) categories.set(handle, new Set());
    categories.get(handle).add(panel.dataset.collectionHandle);
  }));
  const popular = [...(root.querySelector('[data-fp-popular-pool]')?.content.querySelectorAll('[data-discovery-card]') || [])];
  const popularity = new Map(popular.map((card, index) => [card.dataset.productHandle, 1 - index / Math.max(1, popular.length)]));
  const random = new WeakMap();
  const scoreCache = new WeakMap();
  const affinityCache = new Map();
  const interest = (kind, value) => {
    const key = kind + ':' + value;
    if (!affinityCache.has(key)) affinityCache.set(key, personalization?.affinity(kind, value) || 0);
    return affinityCache.get(key);
  };
  const score = card => {
    if (scoreCache.has(card)) return scoreCache.get(card);
    if (!random.has(card)) random.set(card, Math.random());
    const type = interest('type', card.dataset.productType);
    const category = Math.max(0, ...[...(categories.get(card.dataset.productHandle) || [])].map(value => interest('category', value)));
    const age = Math.max(0, (Date.now() / 1000 - Number(card.dataset.publishedAt || 0)) / 86400);
    const fresh = Math.max(0, 1 - age / 30);
    const tags = (card.dataset.seasons || '').toLowerCase().split(',');
    const month = new Date().getMonth();
    const season = month === 11 || month < 2 ? 'winter' : month < 5 ? 'spring' : month < 8 ? 'summer' : 'fall';
    const seasonal = tags.includes(season) || (month >= 9 && tags.includes('winter')) ? 1 : 0;
    const repeat = personalization?.wasSeen(card.dataset.productHandle) ? 1 : 0;
    const purchase = personalization?.purchaseAffinity(card.dataset.productType) || 0;
    const value = 4 * Math.max(type, category) + 2 * fresh + seasonal + purchase + .5 * (popularity.get(card.dataset.productHandle) || 0) + (card.dataset.nativeIntent ? 2 : 0) - 2 * repeat + random.get(card) * 2;
    scoreCache.set(card, value);
    return value;
  };
  const reorder = (panel, extras = []) => {
    const grid = panel.querySelector('.fp-discovery__grid');
    if (!grid) return;
    const pool = [...allCards(panel), ...extras].filter(card => card.dataset.recommendationEligible !== 'false').sort((a, b) => score(b) - score(a));
    const cards = [];
    while (pool.length) {
      const recent = cards.slice(-5);
      let index = pool.findIndex(card => recent.filter(item => item.dataset.productType === card.dataset.productType).length < 2);
      if (index < 0) index = 0;
      cards.push(pool.splice(index, 1)[0]);
    }
    allCards(panel).filter(card => card.dataset.recommendationEligible === 'false').forEach(card => card.remove());
    grid.replaceChildren(...cards);
  };
  panels.forEach(panel => allCards(panel).forEach(card => { card.dataset.recommendationCategory = panel.dataset.collectionHandle; }));

  const main = panels[0];
  if (main) {
    const grid = main.querySelector('.fp-discovery__grid');
    if (grid) {
      const existing = new Set(allCards(main).map(card => card.dataset.productHandle));
      const extras = [...popular, ...panels.slice(1).flatMap(panel => allCards(panel))]
        .filter(card => {
          const handle = card.dataset.productHandle;
          if (!handle || existing.has(handle)) return false;
          existing.add(handle);
          return true;
        }).sort((a, b) => score(b) - score(a)).slice(0, 24);
      reorder(main, extras.map(card => card.cloneNode(true)));
    }
  }

  root.dataset.rankingMs = (performance.now() - rankStarted).toFixed(1);
  root.dataset.personalizationMode = personalization?.allowed() ? 'personalized' : 'general';
  const exposureObservers = new Set();
  const exposureObserved = new WeakSet();
  const markVisible = panel => {
    if (!panel || !personalization?.allowed() || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(entries => {
      const handles = entries.filter(e => e.isIntersecting).map(e => { observer.unobserve(e.target); return e.target.dataset.productHandle; });
      personalization.seen(handles.filter(Boolean));
    }, {threshold: .5});
    exposureObservers.add(observer);
    allCards(panel).filter(card => !exposureObserved.has(card)).forEach(card => { exposureObserved.add(card); observer.observe(card); });
  };
  markVisible(main);
  let nativeQueue = [];
  let nativeController;
  let nativeSeed = '';
  let nativeGeneration = 0;
  const takeNative = () => {
    if (!personalization?.allowed()) return [];
    const present = new Set(main ? allCards(main).map(card => card.dataset.productHandle) : []);
    nativeQueue = nativeQueue.filter(card => !present.has(card.dataset.productHandle));
    return nativeQueue.splice(0, 4);
  };
  const loadNative = async () => {
    if (!main || !root.dataset.nativeRecommendationsUrl || !personalization?.allowed()) return;
    const seed = personalization.latestProductId();
    if (!/^\d+$/.test(seed) || seed === nativeSeed) return;
    nativeController?.abort();
    const controller = new AbortController();
    nativeController = controller;
    nativeSeed = seed;
    const generation = ++nativeGeneration;
    const timer = setTimeout(() => controller.abort(), 4000);
    try {
      for (const intent of ['complementary', 'related']) {
        const url = new URL(root.dataset.nativeRecommendationsUrl, location.origin);
        url.searchParams.set('product_id', seed);
        url.searchParams.set('limit', '10');
        url.searchParams.set('intent', intent);
        url.searchParams.set('section_id', 'fiive-recommendation-candidates');
        const response = await fetch(url, {credentials: 'same-origin', signal: controller.signal});
        if (!response.ok) continue;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!personalization.allowed() || generation !== nativeGeneration) return;
        const candidates = [...doc.querySelectorAll('[data-fp-native-candidates] [data-discovery-card]')].filter(card => card.dataset.recommendationEligible === 'true');
        if (!candidates.length) continue;
        candidates.forEach(card => { card.dataset.nativeIntent = intent; });
        nativeQueue = candidates;
        root.dataset.nativeCandidates = String(candidates.length);
        root.dataset.nativeIntent = intent;
        enableMix();
        break;
      }
    } catch {} finally { clearTimeout(timer); }
  };
  const idleNative = () => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(loadNative, {timeout: 1500});
    else setTimeout(loadNative, 100);
  };
  document.addEventListener('fiive:personalization-change', () => {
    root.dataset.personalizationMode = personalization?.allowed() ? 'personalized' : 'general';
    if (!personalization?.allowed()) {
      nativeGeneration++;
      nativeController?.abort();
      nativeSeed = '';
      nativeQueue = [];
      root.dataset.nativeCandidates = '0';
      exposureObservers.forEach(observer => observer.disconnect());
    } else { markVisible(root.querySelector('[data-discovery-panel].is-active')); idleNative(); }
  });
  const state = new WeakMap();
  const load = async (panel, force = false) => {
    const next = panel.querySelector('[data-discovery-next]');
    if (!next || state.get(panel) === 'loading' || (!force && !panel.classList.contains('is-active'))) return;
    state.set(panel, 'loading');
    next.textContent = 'Loading more…';
    next.setAttribute('aria-disabled', 'true');
    try {
      const url = new URL(next.href, location.href);
      url.searchParams.set('section_id', root.dataset.sectionId);
      const preview = new URLSearchParams(location.search).get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const response = await fetch(url, {headers: {'Accept': 'text/html'}, credentials: 'same-origin'});
      if (!response.ok) throw new Error('Feed unavailable');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const remote = doc.querySelector('[data-fp-discovery] [data-discovery-panel][data-collection-handle="' +
        CSS.escape(panel.dataset.collectionHandle || '') + '"]');
      if (!remote) throw new Error('Feed unavailable');
      const incoming = allCards(remote).filter(card => card.dataset.recommendationEligible !== 'false');
      incoming.forEach(card => { card.dataset.recommendationCategory = panel.dataset.collectionHandle; });
      const grid = panel.querySelector('.fp-discovery__grid');
      const present = new Set(allCards(panel).map(card => card.dataset.productHandle));
      const unique = [...(panel === main ? takeNative() : []), ...incoming].filter(card => {
        const handle = card.dataset.productHandle;
        if (!handle || present.has(handle)) return false;
        present.add(handle);
        if (!categories.has(handle)) categories.set(handle, new Set());
        categories.get(handle).add(panel.dataset.collectionHandle);
        return true;
      })
        .sort((a, b) => score(b) - score(a));
      grid.append(...unique.map(card => document.importNode(card, true)));
      const further = remote.querySelector('[data-discovery-next]');
      if (further && incoming.length) {
        next.href = further.href;
        next.textContent = 'Load more products';
        next.removeAttribute('aria-disabled');
      } else {
        next.remove();
        if (panel === main) enableMix();
      }
      state.delete(panel);
      markVisible(panel);
      window.dispatchEvent(new CustomEvent('wishlist:refresh'));
    } catch (_) {
      state.delete(panel);
      next.textContent = 'Load more products';
      next.removeAttribute('aria-disabled');
    }
  };


  let mixButton = null;
  let mixLoading = false;
  const fillMain = async () => {
    if (!main || mixLoading || !main.classList.contains('is-active')) return;
    mixLoading = true;
    const grid = main.querySelector('.fp-discovery__grid');
    if (!grid) { mixLoading = false; return; }
    let present = new Set(allCards(main).map(card => card.dataset.productHandle));
    let candidates = [...takeNative(), ...panels.slice(1).flatMap(panel => allCards(panel))]
      .filter(card => card.dataset.productHandle && !present.has(card.dataset.productHandle));
    if (!candidates.length) {
      const source = panels.slice(1).find(panel => panel.querySelector('[data-discovery-next]'));
      if (source) {
        await load(source, true);
        candidates = panels.slice(1).flatMap(panel => allCards(panel))
          .filter(card => card.dataset.productHandle && !present.has(card.dataset.productHandle));
      }
    }
    if (candidates.length) {
      candidates.sort((a, b) => score(b) - score(a));
      const selected = candidates.filter(card => {
        if (present.has(card.dataset.productHandle)) return false;
        present.add(card.dataset.productHandle);
        return true;
      }).slice(0, 12);
      grid.append(...selected.map(card => card.cloneNode(true)));
      markVisible(main);
      window.dispatchEvent(new CustomEvent('wishlist:refresh'));
    } else if (!nativeQueue.length && !panels.slice(1).some(panel => panel.querySelector('[data-discovery-next]'))) {
      mixButton?.remove();
      mixButton = null;
    }
    mixLoading = false;
  };
  const enableMix = () => {
    if (!main || mixButton || main.querySelector('[data-discovery-next]')) return;
    mixButton = document.createElement('button');
    mixButton.type = 'button';
    mixButton.className = 'fp-discovery__next';
    mixButton.textContent = 'Keep discovering';
    main.querySelector('.fp-discovery__grid')?.after(mixButton);
    mixButton.addEventListener('click', fillMain);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(entries => {
        if (entries[0]?.isIntersecting) fillMain();
      }, {rootMargin: '600px 0px'}).observe(mixButton);
    }
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) load(entry.target.closest('[data-discovery-panel]'));
    }), {rootMargin: '700px 0px'})
    : null;
  enableMix();
  idleNative();
  panels.forEach(panel => {
    const next = panel.querySelector('[data-discovery-next]');
    if (!next) return;
    observer?.observe(next);
    next.addEventListener('click', event => {
      event.preventDefault();
      load(panel);
    });
  });
  root.addEventListener('click', event => {
    if (!event.target.closest('[data-discovery-tab]')) return;
    window.setTimeout(() => {
      const panel = root.querySelector('.fp-discovery__panel.is-active');
      if (panel) load(panel);
    }, 100);
  });


})();

